import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface GuestCardData {
  fullName: string;
  ticketNumber: string;
  tierName: string | null;
  tableName: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  zoneName: string | null;
  orderReferenceId: string | null;
  chapterAffiliation: string | null;
  mealSelection: string | null;
  isCheckedIn: boolean;
  checkedInAt: string | null;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://kdu-hub-production.up.railway.app';

export function GuestCardPage() {
  const { ticketNumber } = useParams<{ ticketNumber: string }>();
  const [data, setData] = useState<GuestCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ticketNumber) { setError('No ticket number provided.'); setLoading(false); return; }
    async function fetchCard() {
      try {
        const res = await fetch(`${API_BASE}/guest-card/${encodeURIComponent(ticketNumber!)}`);
        if (!res.ok) { setError(res.status === 404 ? 'Ticket not found.' : 'Failed to load guest info.'); return; }
        const json = await res.json();
        setData(json);
      } catch { setError('Unable to connect. Please try again.'); }
      finally { setLoading(false); }
    }
    fetchCard();
  }, [ticketNumber]);

  if (loading) return (
    <div style={styles.page}>
      <div style={styles.card}><p style={{ textAlign: 'center', color: '#666', fontSize: '1rem' }}>Loading your info...</p></div>
    </div>
  );

  if (error) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>😔</div>
          <p style={{ color: '#d32f2f', fontSize: '1.1rem', fontWeight: 600 }}>{error}</p>
          <p style={{ color: '#666', fontSize: '0.85rem' }}>Please check your QR code or contact an organizer.</p>
        </div>
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Header with KDU branding */}
        <div style={styles.header}>
          <img src="/KDULogo.PNG" alt="KDU" style={styles.logo} />
          <div style={styles.eventTitle}>KDU Georgia 2026</div>
          <div style={styles.eventSubtitle}>Legacy Ball</div>
        </div>

        {/* Guest Name */}
        <div style={styles.nameSection}>
          <div style={styles.guestName}>{data.fullName}</div>
          <div style={styles.ticketBadge}>{data.ticketNumber}{data.tierName ? ` • ${data.tierName}` : ''}</div>
        </div>

        {/* Info Grid */}
        <div style={styles.infoGrid}>
          {(data.tableName || data.tableNumber != null) && (
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>🪑</span>
              <div>
                <div style={styles.infoLabel}>Table / Seat</div>
                <div style={styles.infoValue}>{data.tableName || `Table ${data.tableNumber}`}, Seat {data.seatNumber}</div>
              </div>
            </div>
          )}

          {data.zoneName && (
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>🏷️</span>
              <div>
                <div style={styles.infoLabel}>Zone</div>
                <div style={styles.infoValue}>{data.zoneName}</div>
              </div>
            </div>
          )}

          {data.mealSelection && (
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>🍽️</span>
              <div>
                <div style={styles.infoLabel}>Meal</div>
                <div style={styles.infoValue}>{data.mealSelection}</div>
              </div>
            </div>
          )}

          {data.chapterAffiliation && (
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>📍</span>
              <div>
                <div style={styles.infoLabel}>Chapter</div>
                <div style={styles.infoValue}>{data.chapterAffiliation}</div>
              </div>
            </div>
          )}

          {data.orderReferenceId && (
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>📦</span>
              <div>
                <div style={styles.infoLabel}>Order</div>
                <div style={styles.infoValue}>{data.orderReferenceId}</div>
              </div>
            </div>
          )}

          <div style={styles.infoRow}>
            <span style={styles.infoIcon}>{data.isCheckedIn ? '✅' : '⬜'}</span>
            <div>
              <div style={styles.infoLabel}>Check-In</div>
              <div style={{ ...styles.infoValue, color: data.isCheckedIn ? '#16a34a' : '#d97706' }}>
                {data.isCheckedIn ? `Checked in${data.checkedInAt ? ` • ${new Date(data.checkedInAt).toLocaleString()}` : ''}` : 'Not checked in yet'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p>Welcome to the Legacy Ball! 🎉</p>
          <p style={{ fontSize: '0.7rem', color: '#999' }}>Scan your QR code at the door for check-in</p>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    background: 'linear-gradient(135deg, #0f2d1f 0%, #1a4a2e 50%, #0d2418 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: '#fff',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  header: {
    textAlign: 'center' as const,
    padding: '24px 20px 16px',
    background: 'linear-gradient(135deg, #1a6b3c, #0f4a28)',
    color: '#fff',
  },
  logo: {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    marginBottom: '8px',
    objectFit: 'contain' as const,
  },
  eventTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  eventSubtitle: {
    fontSize: '0.85rem',
    opacity: 0.85,
    marginTop: '2px',
  },
  nameSection: {
    textAlign: 'center' as const,
    padding: '20px 20px 12px',
    borderBottom: '1px solid #e5e7eb',
  },
  guestName: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color: '#0f2d1f',
    marginBottom: '6px',
  },
  ticketBadge: {
    display: 'inline-block',
    padding: '4px 14px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#166534',
  },
  infoGrid: {
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '14px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  infoIcon: {
    fontSize: '1.3rem',
    lineHeight: 1.4,
    flexShrink: 0,
  },
  infoLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  infoValue: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#111827',
    marginTop: '1px',
  },
  footer: {
    textAlign: 'center' as const,
    padding: '16px 20px 20px',
    borderTop: '1px solid #e5e7eb',
    fontSize: '0.85rem',
    color: '#666',
  },
};
